import { editorFontFamily } from '@/lib/editorFonts';
import { pageOverlayStyle, localPoint, localDelta } from '@/lib/pageGeometry';
import { trackPointerDrag } from '@/lib/pointerDrag';
import { useState, useRef, useCallback, useEffect, useLayoutEffect } from 'react';
import { X, ArrowRightLeft, Lock } from 'lucide-react';
import { editToSegments, segmentsToText, getStylesAtOffset, mergeAdjacentSegments, DEFAULT_SEGMENT_STYLES } from '@/lib/richText';

/** Map PDF standard font IDs to CSS font-family stacks for browser rendering. */
const CSS_FONT_MAP = {
  'Arial': 'Arial, Helvetica, sans-serif',
  'Helvetica': 'Helvetica, Arial, sans-serif',
  'Times-Roman': '"Times New Roman", Times, serif',
  'Courier': '"Courier New", Courier, monospace',
  'Roboto Slab': '"Roboto Slab", serif',
};

function cssFontFamily(pdfFontId) {
  return CSS_FONT_MAP[pdfFontId] || `${pdfFontId}, sans-serif`;
}

/**
 * Guard for the auto-width-fit useLayoutEffect in ContentEditableText.
 * Detected blocks got their width from detection — never auto-fit them on
 * mount; doing so marks untouched originals `modified` (= destructive at
 * save time, see useEdits' needsModifiedFlag). Fitting still applies once
 * the user actually edits (isEditing true) or for non-detected (user-added)
 * edits, which have no "untouched original" to protect.
 * Pure + exported so it can be unit-tested without a full render harness.
 */
export function shouldAutoFitOnMount(edit, isEditing) {
  return Boolean(isEditing && !edit?.restored && edit?.source !== 'detected');
}

/** Generate locale-formatted date string. Falls back to MM/DD/YYYY. */
function getFormattedDate() {
  try {
    return new Intl.DateTimeFormat(navigator.language, {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date());
  } catch (_) {
    const d = new Date();
    return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
  }
}

/**
 * Resize handle definitions.
 * cx/cy encode which edges are affected:
 *   -1 = left/top (position moves, size shrinks)
 *    0 = center (no change on that axis)
 *    1 = right/bottom (size grows)
 */
const HANDLES = [
  { id: 'tl', cursor: 'nwse-resize', cx: -1, cy: -1, isCorner: true },
  { id: 'tc', cursor: 'ns-resize',   cx:  0, cy: -1, isCorner: false },
  { id: 'tr', cursor: 'nesw-resize', cx:  1, cy: -1, isCorner: true },
  { id: 'lc', cursor: 'ew-resize',   cx: -1, cy:  0, isCorner: false },
  { id: 'rc', cursor: 'ew-resize',   cx:  1, cy:  0, isCorner: false },
  { id: 'bl', cursor: 'nesw-resize', cx: -1, cy:  1, isCorner: true },
  { id: 'bc', cursor: 'ns-resize',   cx:  0, cy:  1, isCorner: false },
  { id: 'br', cursor: 'nwse-resize', cx:  1, cy:  1, isCorner: true },
];

/** Position a handle element relative to the item's bounding box. */
function handlePosition(handle, zoom) {
  const size = 8;
  const half = size / 2;
  const xFrac = (handle.cx + 1) / 2; // 0, 0.5, or 1
  const yFrac = (handle.cy + 1) / 2;
  return {
    position: 'absolute',
    width: `${size}px`,
    height: `${size}px`,
    left: `calc(${xFrac * 100}% - ${half}px)`,
    top: `calc(${yFrac * 100}% - ${half}px)`,
    cursor: handle.cursor,
    zIndex: 10,
  };
}

/**
 * Get the character offset range of the current selection within a contenteditable element.
 * Returns { start, end } where both are character indices, or null if no selection.
 */
function getSelectionOffsets(editableEl) {
  const sel = window.getSelection();
  if (!sel.rangeCount || !editableEl.contains(sel.anchorNode)) return null;

  const range = sel.getRangeAt(0);

  const preRange = document.createRange();
  preRange.selectNodeContents(editableEl);
  preRange.setEnd(range.startContainer, range.startOffset);
  const start = preRange.toString().length;

  const fullRange = document.createRange();
  fullRange.selectNodeContents(editableEl);
  fullRange.setEnd(range.endContainer, range.endOffset);
  const end = fullRange.toString().length;

  return { start, end };
}

/**
 * Set selection on a contenteditable element by character offsets.
 */
function setSelectionOffsets(editableEl, start, end) {
  const walker = document.createTreeWalker(editableEl, NodeFilter.SHOW_TEXT, null);
  let charIndex = 0;
  let startNode = null, startOffset = 0, endNode = null, endOffset = 0;

  let node;
  while ((node = walker.nextNode())) {
    const nodeLen = node.textContent.length;
    if (!startNode && charIndex + nodeLen >= start) {
      startNode = node;
      startOffset = start - charIndex;
    }
    if (charIndex + nodeLen >= end) {
      endNode = node;
      endOffset = end - charIndex;
      break;
    }
    charIndex += nodeLen;
  }

  if (startNode && endNode) {
    const sel = window.getSelection();
    const range = document.createRange();
    range.setStart(startNode, startOffset);
    range.setEnd(endNode, endOffset);
    sel.removeAllRanges();
    sel.addRange(range);
  }
}

/**
 * Read segments back from the contenteditable DOM.
 * Each <span> with data-styles corresponds to a segment. Handles text nodes outside spans.
 */
function domToSegments(editableEl, fallbackStyles) {
  const segments = [];
  for (const child of editableEl.childNodes) {
    if (child.nodeType === Node.TEXT_NODE) {
      if (child.textContent) {
        segments.push({ text: child.textContent, styles: { ...fallbackStyles } });
      }
    } else if (child.nodeType === Node.ELEMENT_NODE) {
      if (child.tagName === 'BR') {
        segments.push({ text: '\n', styles: { ...(segments.length > 0 ? segments[segments.length - 1].styles : fallbackStyles) } });
        continue;
      }
      // Chrome wraps Enter-key lines in <div>; treat as newline + recurse
      if (child.tagName === 'DIV') {
        if (segments.length > 0 && segments[segments.length - 1].text !== '\n') {
          segments.push({ text: '\n', styles: { ...(segments.length > 0 ? segments[segments.length - 1].styles : fallbackStyles) } });
        }
        const inner = domToSegments(child, fallbackStyles);
        segments.push(...inner);
        continue;
      }
      const text = child.textContent || '';
      if (!text) continue;
      let styles;
      try {
        styles = JSON.parse(child.dataset.styles || '{}');
        for (const key of Object.keys(DEFAULT_SEGMENT_STYLES)) {
          if (styles[key] === undefined) styles[key] = fallbackStyles[key] ?? DEFAULT_SEGMENT_STYLES[key];
        }
      } catch (_) {
        styles = { ...fallbackStyles };
      }
      segments.push({ text, styles });
    }
  }
  if (segments.length === 0) {
    segments.push({ text: '', styles: { ...fallbackStyles } });
  }
  return mergeAdjacentSegments(segments);
}

// A user-placed text box left untouched ("Insert text") or emptied is
// clutter, not content — drop it when editing ends. Detected (original PDF)
// blocks are never auto-removed: emptying one is a deliberate text delete.
export function isAbandonedTextBox(edit) {
  if (edit.type !== 'text' || edit.source === 'detected') return false;
  const text = (edit.segments ? edit.segments.map((sg) => sg.text).join('') : edit.content) ?? '';
  if (!text.trim()) return true;
  return Boolean(edit.placeholder) && text === 'Insert text';
}

/**
 * EditLayer — Overlay for text and image edits on a single PDF page.
 *
 * Renders edit items (text boxes, images) as absolutely positioned HTML
 * elements. Supports click-to-place, drag-to-move, resize via 8 handles,
 * multi-select with Shift+click, and multi-drag.
 * Coordinates are in page space (top-left origin, scale=1).
 */
export default function EditLayer({
  pageNum,
  numPages,
  geometry,
  pageWidth,
  pageHeight,
  zoom,
  edits,
  editTool,
  editStyles,
  selectedEditIds,
  signatureDataUrl,
  candidates,
  onPromoteCandidate,
  onAddEdit,
  onUpdateEdit,
  onUpdateEdits,
  onCommitUpdate,
  onSelectEdit,
  onToggleSelect,
  onDeselectAll,
  onDeleteEdit,
  onOpenSignaturePanel,
  onDragStart,
  onSetTool,
  onSelectionChange,
  onSegmentStyleChange,
  annotationDrawing,
}) {
  const layerRef = useRef(null);
  const itemMouseDownRef = useRef(false); // tracks if an item mousedown just fired
  // Whether a box was active when this click's mousedown landed. Read at
  // mousedown because that mousedown's blur ends/removes the box and clears
  // the selection before the click event arrives.
  const dismissClickRef = useRef(false);

  // Text-tool clicks may land on the page itself or on an image/signature —
  // typing a label over a picture is a normal thing to want.
  const isPlaceableTarget = useCallback((target) =>
    target === layerRef.current || Boolean(target?.closest?.('[data-edit-kind="image"]')), []);
  const textBoxActive = useCallback(() =>
    edits.some((ed) => ed.type === 'text' && selectedEditIds.has(ed.id)), [edits, selectedEditIds]);

  const toPageCoords = useCallback(
    (e) => {
      const rect = layerRef.current.getBoundingClientRect();
      return localPoint(e, rect, zoom, pageWidth, pageHeight, geometry?.rotation);
    },
    [pageWidth, pageHeight, zoom, geometry]
  );

  // Click on empty area: place text or deselect
  const handleLayerClick = useCallback(
    (e) => {
      // If an item mousedown just fired, the click bubbles to the layer
      // due to e.preventDefault() on mousedown changing the click target. Skip it.
      if (itemMouseDownRef.current) {
        itemMouseDownRef.current = false;
        return;
      }
      // Only handle clicks on the layer background (or, for the Text tool,
      // on an image) — not on text boxes or other child elements.
      if (e.target !== layerRef.current && !(editTool === 'text' && isPlaceableTarget(e.target))) return;

      if (editTool === 'text') {
        // A click that finishes the current box (or the 2nd click of a
        // double-click aimed at existing text) must not drop a fresh
        // "Insert text" box — first click dismisses, next one places.
        if (e.detail > 1) return;
        const dismiss = dismissClickRef.current || textBoxActive();
        dismissClickRef.current = false;
        if (dismiss) {
          onDeselectAll();
          return;
        }
        const { x, y } = toPageCoords(e);
        const h = Math.max(24, editStyles.fontSize * 1.6);
        onAddEdit({
          type: 'text',
          pageNum,
          x,
          y: Math.max(0, y - h / 2),
          width: 150,
          height: h,
          content: 'Insert text',
          placeholder: true,
          styles: { ...editStyles },
          editing: true,
          selectAll: true,
        });
      } else if (editTool === 'date') {
        const { x, y } = toPageCoords(e);
        const h = Math.max(18, editStyles.fontSize * 1.4);
        onAddEdit({
          type: 'text',
          pageNum,
          x,
          y: Math.max(0, y - h / 2),
          width: Math.max(100, editStyles.fontSize * 7),
          height: h,
          content: getFormattedDate(),
          styles: { ...editStyles },
          editing: false,
        });
      } else if (editTool === 'signature') {
        if (!signatureDataUrl) {
          if (onOpenSignaturePanel) onOpenSignaturePanel();
          return;
        }
        const { x, y } = toPageCoords(e);
        const img = new window.Image();
        img.src = signatureDataUrl;
        const natW = img.naturalWidth || 300;
        const natH = img.naturalHeight || 80;
        const scale = Math.min(150 / natW, 40 / natH, 1);
        const w = natW * scale;
        const h = natH * scale;
        onAddEdit({
          type: 'signature',
          pageNum,
          x: Math.max(0, x - w / 2),
          y: Math.max(0, y - h / 2),
          width: w,
          height: h,
          content: signatureDataUrl,
          styles: {},
        });
      } else {
        onDeselectAll();
      }
    },
    [editTool, editStyles, pageNum, toPageCoords, onAddEdit, onDeselectAll, signatureDataUrl, onOpenSignaturePanel, isPlaceableTarget, textBoxActive]
  );

  // --- Drag / resize handlers ---

  // Resize listeners attach synchronously at mousedown (see lib/pointerDrag):
  // from an effect, a quick grab-and-release could lose its mouseup and leave
  // the handle following the bare pointer.
  const resizeDeps = useRef({});
  resizeDeps.current = { pageWidth, pageHeight, onUpdateEdit, onCommitUpdate, geometry };
  const resizeTrackerRef = useRef(null);
  const beginResize = useCallback((rs) => {
    resizeTrackerRef.current?.stop();
    const handleMouseMove = (e) => {
      const { pageWidth, pageHeight, onUpdateEdit, geometry } = resizeDeps.current;
      const { x: dx, y: dy } = localDelta((e.clientX - rs.startMouseX) * rs.scaleX, (e.clientY - rs.startMouseY) * rs.scaleY, geometry?.rotation);
      const h = rs.handle;
      let newX = rs.startX, newY = rs.startY, newW = rs.startW, newH = rs.startH;

      if (h.cx === -1) { newX += dx; newW -= dx; }
      else if (h.cx === 1) { newW += dx; }
      if (h.cy === -1) { newY += dy; newH -= dy; }
      else if (h.cy === 1) { newH += dy; }

      if ((rs.type === 'image' || rs.type === 'signature') && h.isCorner && !e.shiftKey) {
        // Use horizontal delta only for smooth proportional resize (avoids axis oscillation)
        newH = newW / rs.aspect;
        if (h.cy === -1) newY = rs.startY + rs.startH - newH;
        if (h.cx === -1) newX = rs.startX + rs.startW - newW;
      }

      const minW = rs.type === 'text' ? 50 : 20;
      const minH = 20;
      if (newW < minW) { newW = minW; if (h.cx === -1) newX = rs.startX + rs.startW - minW; }
      if (newH < minH) { newH = minH; if (h.cy === -1) newY = rs.startY + rs.startH - minH; }
      if (newX < 0) { newW += newX; newX = 0; }
      if (newY < 0) { newH += newY; newY = 0; }
      if (newX + newW > pageWidth) newW = pageWidth - newX;
      if (newY + newH > pageHeight) newH = pageHeight - newY;

      onUpdateEdit(rs.editId, { x: newX, y: newY, width: newW, height: newH });
    };
    resizeTrackerRef.current = trackPointerDrag({
      onMove: handleMouseMove,
      onEnd: () => {
        resizeTrackerRef.current = null;
        resizeDeps.current.onCommitUpdate?.();
      },
    });
  }, []);

  useEffect(() => () => resizeTrackerRef.current?.stop(), []);

  const onDragStartRef = useRef(onDragStart);
  onDragStartRef.current = onDragStart;

  const handleItemMouseDown = useCallback(
    (editId, action, handle, e) => {
      // Text tool over an image/signature: let the press fall through to the
      // layer so the click places a text box there instead of grabbing the
      // image. Default is kept so an active text box still blurs.
      if (editTool === 'text' && action === 'move' && !e.shiftKey) {
        const target = edits.find((ed) => ed.id === editId);
        if (target && target.type !== 'text') return;
      }
      e.stopPropagation();
      e.preventDefault();
      itemMouseDownRef.current = true;

      // Shift+click: toggle multi-select
      if (e.shiftKey && action === 'move') {
        onToggleSelect(editId);
        return;
      }

      // If clicking an unselected item without shift, select only that item
      if (!selectedEditIds.has(editId)) {
        onSelectEdit(editId);
      }

      const edit = edits.find((ed) => ed.id === editId);
      if (!edit) return;

      const rect = layerRef.current.getBoundingClientRect();
      const scaleX = 1 / zoom;
      const scaleY = 1 / zoom;

      if (action === 'resize') {
        beginResize({
          editId,
          handle: handle || null,
          startMouseX: e.clientX,
          startMouseY: e.clientY,
          startX: edit.x,
          startY: edit.y,
          startW: edit.width,
          startH: edit.height,
          scaleX,
          scaleY,
          aspect: edit.width / edit.height,
          type: edit.type,
        });
      } else if (action === 'move') {
        // Defer drag start until mouse moves beyond 3px threshold
        const mouseDownX = e.clientX;
        const mouseDownY = e.clientY;
        const DRAG_THRESHOLD = 3;

        const startPositions = new Map();
        const currentIds = selectedEditIds.has(editId) ? selectedEditIds : new Set([editId]);
        for (const id of currentIds) {
          const ed = edits.find((item) => item.id === id);
          if (ed && ed.pageNum === pageNum) {
            startPositions.set(id, { x: ed.x, y: ed.y, width: ed.width, height: ed.height });
          }
        }

        const dragInfo = {
          editId,
          pageNum,
          startMouseX: mouseDownX,
          startMouseY: mouseDownY,
          startX: edit.x,
          startY: edit.y,
          startW: edit.width,
          startH: edit.height,
          scaleX,
          scaleY,
          startPositions,
          multiDrag: startPositions.size > 1,
          rotation: geometry?.rotation || 0,
          // The page's on-screen rect AT MOUSEDOWN, so the grab offset is
          // measured against what the user actually pressed on even if the
          // layout shifts before the drag threshold is crossed.
          startSurfaceRect: (layerRef.current.closest('[data-page]') || layerRef.current).getBoundingClientRect(),
        };

        const onMove = (me) => {
          const dx = me.clientX - mouseDownX;
          const dy = me.clientY - mouseDownY;
          if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
            cleanup();
            itemMouseDownRef.current = false; // reset — drag takes over
            if (onDragStartRef.current) onDragStartRef.current(dragInfo, me);
          }
        };
        const onUp = () => {
          // Pure click — selection already applied, nothing else to do
          cleanup();
        };
        const cleanup = () => {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      }
    },
    [edits, pageWidth, pageHeight, pageNum, selectedEditIds, onSelectEdit, onToggleSelect, zoom, geometry, beginResize, editTool]
  );


  const pageEdits = edits.filter((e) => e.pageNum === pageNum && !e.deleted);

  return (
    <div
      ref={layerRef}
      className="absolute inset-0"
      style={{
        ...pageOverlayStyle(geometry, zoom),
        width: `${pageWidth * zoom}px`,
        height: `${pageHeight * zoom}px`,
        pointerEvents: (editTool === 'text' || editTool === 'date' || editTool === 'signature') ? 'auto' : 'none',
        cursor: (editTool === 'text' || editTool === 'date' || editTool === 'signature') ? 'crosshair' : 'default',
      }}
      onMouseDown={(e) => {
        if (isPlaceableTarget(e.target)) dismissClickRef.current = textBoxActive();
      }}
      onClick={handleLayerClick}
    >
      {pageEdits.map((edit) => (
        <EditItem
          onCommitUpdate={onCommitUpdate}
          key={edit.id}
          edit={edit}
          zoom={zoom}
          pageWidth={pageWidth}
          numPages={numPages}
          isSelected={selectedEditIds.has(edit.id)}
          interactive={!!editTool && !annotationDrawing}
          textPassThrough={editTool === 'text' && edit.type !== 'text'}
          onMouseDown={(action, handle, e) => handleItemMouseDown(edit.id, action, handle, e)}
          onUpdateEdit={onUpdateEdit}
          onDeleteEdit={onDeleteEdit}
          onSelectEdit={onSelectEdit}
          onToggleSelect={onToggleSelect}
          onSelectionChange={onSelectionChange}
          onSegmentStyleChange={onSegmentStyleChange}
        />
      ))}
      {!!candidates && candidates.length > 0 && candidates.map((block, i) => (
        <CandidateItem
          key={block.id || `candidate-${pageNum}-${i}`}
          block={block}
          zoom={zoom}
          interactive={!!editTool && !annotationDrawing}
          onPromote={(intent) => onPromoteCandidate?.(block, intent)}
        />
      ))}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// EditItem — Individual text box or image overlay
// ────────────────────────────────────────────────────────────────────

function EditItem({ edit, onCommitUpdate, zoom, pageWidth, numPages, isSelected, interactive, textPassThrough, onMouseDown, onUpdateEdit, onDeleteEdit, onSelectEdit, onToggleSelect, onSelectionChange, onSegmentStyleChange }) {
  const [isEditing, setIsEditing] = useState(edit.editing || false);
  const [isHovered, setIsHovered] = useState(false);
  const [contextMenu, setContextMenu] = useState(null); // { x, y }
  const textRef = useRef(null);
  const menuRef = useRef(null);
  const textWrapperRef = useRef(null);

  // Close context menu on outside click
  useEffect(() => {
    if (!contextMenu) return;
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };
    window.addEventListener('mousedown', handleClick);
    return () => window.removeEventListener('mousedown', handleClick);
  }, [contextMenu]);

  const style = {
    position: 'absolute',
    left: `${edit.x * zoom}px`,
    top: `${edit.y * zoom}px`,
    width: `${edit.width * zoom}px`,
    height: `${edit.height * zoom}px`,
  };

  const handleClick = (e) => {
    // Text tool over an image: the layer places a text box at this click.
    if (textPassThrough) return;
    e.stopPropagation();
    if (!interactive) return;
    // Shift+click is handled in onMouseDown → handleItemMouseDown.
    if (e.shiftKey) return;
    onSelectEdit(edit.id);
  };

  const handleContextMenu = (e) => {
    if (!interactive || !numPages || numPages <= 1) return;
    e.preventDefault();
    e.stopPropagation();
    onSelectEdit(edit.id);
    setContextMenu({ x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY });
  };

  const handleMoveTo = (targetPage) => {
    onUpdateEdit(edit.id, { pageNum: targetPage });
    setContextMenu(null);
  };

  // Show delete button: images on hover OR select, text only on select
  const showDelete = interactive && (edit.type === 'image' ? (isHovered || isSelected) : isSelected);

  // Detected/restored border is hover-only, matching CandidateItem's dormant
  // pattern (~line 759) — an always-on dashed border here meant a promoted
  // or sidecar-restored detected block showed a permanent box every time
  // Scrivar's edit layer was interactive, including right after reopening a
  // saved document with zero user interaction (Preview/Chrome never render
  // this overlay at all, so the bug only showed up inside the app). Selected
  // keeps its own indigo border regardless of hover.
  const wrapperProps = {
    style: {
      ...style,
      outline: isSelected && interactive
        ? '1.5px dashed #4F46E5'
        : edit.source === 'detected' && interactive && isHovered
          ? '1px dashed #93C5FD'
          : '1px dashed transparent',
      cursor: interactive ? (isSelected ? 'move' : 'pointer') : 'default',
      pointerEvents: interactive ? 'auto' : 'none',
    },
    onClick: handleClick,
    onContextMenu: handleContextMenu,
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
  };

  const contextMenuEl = contextMenu && (
    <div
      ref={menuRef}
      className="absolute bg-white border border-gray-200 rounded-lg shadow-lg py-1 z-50"
      style={{ left: `${contextMenu.x}px`, top: `${contextMenu.y}px`, minWidth: '140px' }}
    >
      <div className="px-3 py-1.5 text-xs text-gray-400 font-medium flex items-center gap-1.5">
        <ArrowRightLeft size={12} /> Move to page
      </div>
      <div className="max-h-40 overflow-y-auto">
        {Array.from({ length: numPages }, (_, i) => i + 1).map((p) => (
          <button
            key={p}
            disabled={p === edit.pageNum}
            onClick={(e) => { e.stopPropagation(); handleMoveTo(p); }}
            className={`w-full text-left px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-30 disabled:cursor-not-allowed ${
              p === edit.pageNum ? 'font-medium text-brand-blue' : 'text-gray-700'
            }`}
          >
            Page {p}{p === edit.pageNum ? ' (current)' : ''}
          </button>
        ))}
      </div>
    </div>
  );

  // Rotated text: editing would corrupt it (overlay/save paths are
  // horizontal-only) — render as a protected, non-interactive block.
  if (edit.type === 'text' && edit.rotationLocked) {
    return (
      <div
        style={{
          ...style,
          border: '1px dashed #9CA3AF',
          pointerEvents: 'auto',
          cursor: 'not-allowed',
          position: 'absolute',
        }}
        title="Editing rotated text isn't supported yet — this text is protected so your document isn't damaged."
      >
        <div className="absolute top-1 left-1 bg-gray-700/70 rounded p-0.5">
          <Lock size={12} className="text-white" />
        </div>
      </div>
    );
  }

  if (edit.type === 'text') {
    return (
      <div
        ref={textWrapperRef}
        {...wrapperProps}
        style={{
          ...wrapperProps.style,
          minWidth: `${50 * zoom}px`,
          minHeight: `${20 * zoom}px`,
          height: isEditing ? 'auto' : wrapperProps.style.height,
        }}
        onMouseDown={(e) => { if (interactive && !isEditing) onMouseDown('move', null, e); }}
        onDoubleClick={(e) => { if (interactive && !e.shiftKey) setIsEditing(true); }}
      >
        <ContentEditableText
          edit={edit}
          zoom={zoom}
          pageWidth={pageWidth}
          isEditing={isEditing}
          isSelected={isSelected}
          onStopEditing={() => {
            if (isAbandonedTextBox(edit)) {
              setIsEditing(false);
              onDeleteEdit(edit.id);
              return;
            }
            requestAnimationFrame(() => onCommitUpdate?.());
            // Measure rendered height and persist it so the box keeps its size
            if (textWrapperRef.current) {
              const renderedHeight = textWrapperRef.current.offsetHeight / zoom;
              if (renderedHeight > edit.height) {
                onUpdateEdit(edit.id, { height: renderedHeight, editing: false });
                setIsEditing(false);
                return;
              }
            }
            setIsEditing(false);
            onUpdateEdit(edit.id, { editing: false });
          }}
          onUpdateEdit={onUpdateEdit}
          textRef={textRef}
          onSelectionChange={onSelectionChange}
          onSegmentStyleChange={onSegmentStyleChange}
        />

        {isSelected && interactive && !isEditing && <ResizeHandles onMouseDown={onMouseDown} zoom={zoom} />}
        {showDelete && !isEditing && (
          <DeleteButton onClick={(e) => { e.stopPropagation(); onDeleteEdit(edit.id); }} />
        )}
        {contextMenuEl}
      </div>
    );
  }

  if (edit.type === 'image' || edit.type === 'signature') {
    // Locked edits (flattened images restored from metadata)
    if (edit.locked) {
      return (
        <div
          style={{
            ...style,
            border: '1.5px dashed #9CA3AF',
            pointerEvents: 'auto',
            cursor: 'not-allowed',
            position: 'relative',
          }}
          title="This image was flattened and can't be re-edited"
          data-edit-kind="image"
        >
          {edit.content && edit.content !== '__FLATTENED__' ? (
            <img src={edit.content} alt="Locked edit" className="w-full h-full object-contain opacity-70" draggable={false} />
          ) : (
            <div className="w-full h-full bg-gray-100 flex items-center justify-center">
              <span className="text-xs text-gray-400">Flattened</span>
            </div>
          )}
          <div className="absolute top-1 left-1 bg-gray-700/70 rounded p-0.5">
            <Lock size={12} className="text-white" />
          </div>
        </div>
      );
    }

    return (
      <div
        {...wrapperProps}
        data-edit-kind="image"
        style={{ ...wrapperProps.style, pointerEvents: 'auto' }}
        onMouseDown={(e) => { if (interactive) onMouseDown('move', null, e); }}
      >
        <img
          src={edit.content}
          alt="Edit"
          className="w-full h-full object-contain"
          draggable={false}
        />
        {isSelected && interactive && <ResizeHandles onMouseDown={onMouseDown} zoom={zoom} />}
        {showDelete && (
          <DeleteButton onClick={(e) => { e.stopPropagation(); onDeleteEdit(edit.id); }} />
        )}
        {contextMenuEl}
      </div>
    );
  }

  return null;
}

// ────────────────────────────────────────────────────────────────────
// CandidateItem — A lightweight, non-edit hit-target for a detected but not
// yet promoted text block. Visually identical to today's dormant detected
// EditItem (transparent text over the canvas, dashed #93C5FD outline on
// hover) but carries none of EditItem's machinery — no drag, no resize, no
// contenteditable, no selection. A click promotes the candidate to a real
// edit (intent:'edit'); the hover delete button promotes then immediately
// tombstones it (intent:'delete') — see Viewer's handlePromoteCandidate.
// Rotation-locked blocks render as a locked candidate (Lock badge) and are
// never promotable, reusing EditItem's locked visual treatment.
// ────────────────────────────────────────────────────────────────────

function CandidateItem({ block, zoom, interactive, onPromote }) {
  const [isHovered, setIsHovered] = useState(false);

  const style = {
    position: 'absolute',
    left: `${block.x * zoom}px`,
    top: `${block.y * zoom}px`,
    width: `${block.width * zoom}px`,
    height: `${block.height * zoom}px`,
  };

  if (block.rotated) {
    return (
      <div
        style={{
          ...style,
          border: '1px dashed #9CA3AF',
          pointerEvents: 'auto',
          cursor: 'not-allowed',
        }}
        title="Editing rotated text isn't supported yet — this text is protected so your document isn't damaged."
      >
        <div className="absolute top-1 left-1 bg-gray-700/70 rounded p-0.5">
          <Lock size={12} className="text-white" />
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        ...style,
        border: interactive && isHovered ? '1px dashed #93C5FD' : '1px dashed transparent',
        cursor: interactive ? 'pointer' : 'default',
        pointerEvents: interactive ? 'auto' : 'none',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => {
        e.stopPropagation();
        if (interactive) onPromote('edit');
      }}
    >
      {interactive && isHovered && (
        <DeleteButton onClick={(e) => { e.stopPropagation(); onPromote('delete'); }} />
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// ContentEditableText — Renders text edit segments as styled spans.
// In edit mode, the div is contenteditable and syncs changes back to segments.
// ────────────────────────────────────────────────────────────────────

function ContentEditableText({ edit, zoom, pageWidth, isEditing, isSelected, onStopEditing, onUpdateEdit, textRef, onSelectionChange, onSegmentStyleChange }) {
  const segments = editToSegments(edit);
  const internalRef = useRef(null);
  const savedSelectionRef = useRef(null); // saved selection offsets for restoration after re-render

  // Expose the DOM element through textRef for external focus management
  useEffect(() => {
    if (textRef) textRef.current = internalRef.current;
  }, [textRef]);

  const lastInputRef = useRef(null);
  const lastPresentationRef = useRef(null);
  const composingRef = useRef(false);
  // The browser owns contenteditable children while typing. React reconciling
  // those same spans after Backspace/Enter corrupts the DOM (duplicate lines,
  // stale text and lost selection). Only rebuild for external model changes.
  useLayoutEffect(() => {
    const el = internalRef.current;
    if (!el || composingRef.current) return;
    const model = JSON.stringify(segments);
    const presentation = JSON.stringify([zoom, isEditing, isSelected, edit.source, edit.modified, edit.textLayoutVersion]);
    if (lastInputRef.current === model && lastPresentationRef.current === presentation) return;
    const selection = isEditing ? getSelectionOffsets(el) : null;
    const fragment = document.createDocumentFragment();
    for (const segment of segments) {
      const sty = segment.styles || {};
      const span = document.createElement('span');
      span.dataset.styles = JSON.stringify(sty);
      Object.assign(span.style, {
        fontFamily: edit.textLayoutVersion === 2 ? `"Scrivar ${editorFontFamily(sty.fontFamily)}"` : cssFontFamily(sty.fontFamily || 'Helvetica'),
        fontSize: `${(sty.fontSize || 14) * zoom}px`,
        fontWeight: sty.bold ? 'bold' : 'normal',
        fontStyle: sty.italic ? 'italic' : 'normal',
        color: edit.source === 'detected' && !isEditing && !isSelected && !edit.modified ? 'transparent' : (sty.color || '#000000'),
        textDecoration: [sty.underline && 'underline', sty.strikethrough && 'line-through'].filter(Boolean).join(' ') || 'none',
      });
      span.textContent = segment.text;
      fragment.appendChild(span);
    }
    el.replaceChildren(fragment);
    lastInputRef.current = model;
    lastPresentationRef.current = presentation;
    if (selection && document.activeElement === el) setSelectionOffsets(el, selection.start, selection.end);
  });

  // Re-report selection styles when segments change (e.g., after bold applied).
  // Separate from useLayoutEffect to avoid infinite re-render loop.
  useEffect(() => {
    if (!isEditing || !internalRef.current) return;
    if (document.activeElement !== internalRef.current) return;
    const offsets = getSelectionOffsets(internalRef.current);
    if (!offsets) return;
    const segs = editToSegments(edit);
    const styles = getStylesAtOffset(segs, offsets.start);
    onSelectionChange?.({ editId: edit.id, start: offsets.start, end: offsets.end, styles });
  }, [edit.segments]);

  // On initial edit or selectAll, focus and optionally select all text
  useEffect(() => {
    if (isEditing && internalRef.current) {
      internalRef.current.focus();
      if (edit.selectAll) {
        const sel = window.getSelection();
        const range = document.createRange();
        range.selectNodeContents(internalRef.current);
        sel.removeAllRanges();
        sel.addRange(range);
        onUpdateEdit(edit.id, { selectAll: false });
      }
    }
  }, [isEditing]);

  // On mount (and whenever entering edit mode), fit the box to the rendered
  // content width — the dropped placeholder "Insert text" is much narrower
  // than the 150px default, and any restored edit should also hug its text.
  useLayoutEffect(() => {
    // Detected blocks got their width from detection — never auto-fit them on
    // mount; doing so marks untouched originals `modified` (= destructive at
    // save time). Fitting still applies once the user actually edits.
    if (!shouldAutoFitOnMount(edit, isEditing)) return;
    const el = internalRef.current;
    if (!el || !pageWidth) return;
    const prevWs = el.style.whiteSpace;
    const prevW = el.style.width;
    el.style.whiteSpace = 'nowrap';
    el.style.width = 'max-content';
    const naturalWidth = el.scrollWidth / zoom;
    el.style.whiteSpace = prevWs;
    el.style.width = prevW;
    if (!naturalWidth) return;
    const maxW = pageWidth - edit.x;
    const targetWidth = Math.min(Math.ceil(naturalWidth) + 6, maxW);
    if (Math.abs(targetWidth - edit.width) > 1) {
      onUpdateEdit(edit.id, { width: targetWidth });
    }
  }, [isEditing]);

  // Grow the persisted box with its actual line layout, including after a
  // font/style change. Never resize untouched source text or shrink on reopen.
  useLayoutEffect(() => {
    const el = internalRef.current;
    if (!el || edit.textLayoutVersion !== 2 || edit.source === 'detected' && !edit.modified) return;
    const measure = () => {
      const height = Math.ceil(el.scrollHeight / zoom);
      if (height > edit.height + 0.5) onUpdateEdit(edit.id, { height });
    };
    measure();
    let active = true;
    document.fonts?.ready.then(() => { if (active) measure(); });
    return () => { active = false; };
  }, [edit.segments, edit.styles, edit.width, edit.height, edit.textLayoutVersion, zoom]);

  const handleInput = useCallback(() => {
    const el = internalRef.current;
    if (!el) return;
    const fallbackStyles = segments[0]?.styles || DEFAULT_SEGMENT_STYLES;
    const newSegments = domToSegments(el, fallbackStyles);
    lastInputRef.current = JSON.stringify(newSegments);
    const updates = { textLayoutVersion: 2, segments: newSegments, content: segmentsToText(newSegments) };

    // Auto-fit width to content: shrink AND grow so the box hugs the text.
    // Clamp to the page's right edge so the box can't extend past the page.
    // We temporarily set width:auto + whiteSpace:nowrap so scrollWidth reflects
    // the natural single-line content width — with width:100% (the normal style)
    // scrollWidth would be pinned at the wrapper width whenever the text fits,
    // which kills the shrink branch.
    if (pageWidth) {
      const prevWs = el.style.whiteSpace;
      const prevW = el.style.width;
      el.style.whiteSpace = 'nowrap';
      el.style.width = 'max-content';
      const naturalWidth = el.scrollWidth / zoom;
      el.style.whiteSpace = prevWs;
      el.style.width = prevW;
      const maxW = pageWidth - edit.x;
      // +6 covers the wrapper's 1.5px dashed border on each side (3px),
      // caret space at end of line, and subpixel rounding — without it the
      // editable's width:100% inside the bordered wrapper is one pixel short
      // of the natural width and the text wraps to a second line.
      const targetWidth = Math.min(Math.ceil(naturalWidth) + 6, maxW);
      if (Math.abs(targetWidth - edit.width) > 1) {
        updates.width = targetWidth;
      }
    }

    onUpdateEdit(edit.id, updates);
  }, [edit.id, segments, onUpdateEdit, pageWidth, zoom, edit.x, edit.width]);

  const handleBlur = useCallback((e) => {
    const related = e.relatedTarget;
    if (related && (related.closest('[class*="border-b"]') || related.closest('button') || related.closest('[role="listbox"]'))) {
      setTimeout(() => { if (internalRef.current) internalRef.current.focus(); }, 0);
      return;
    }
    onSelectionChange?.(null);
    onStopEditing();
  }, [onStopEditing, onSelectionChange]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      onSelectionChange?.(null);
      onStopEditing();
      return;
    }
    // Intercept Ctrl+B/I/U to use our segment model instead of browser's execCommand
    if ((e.ctrlKey || e.metaKey) && ['b', 'i', 'u'].includes(e.key.toLowerCase())) {
      e.preventDefault();
      e.stopPropagation();
      const el = internalRef.current;
      if (!el) return;
      const offsets = getSelectionOffsets(el);
      if (!offsets) return;
      const segs = editToSegments(edit);
      const keyMap = { b: 'bold', i: 'italic', u: 'underline' };
      const styleKey = keyMap[e.key.toLowerCase()];
      const currentVal = getStylesAtOffset(segs, offsets.start)[styleKey];
      onSegmentStyleChange?.(styleKey, !currentVal, offsets.start, offsets.end);
    }
  }, [onStopEditing, onSelectionChange, edit, onSegmentStyleChange]);

  // Prevent default paste — insert plain text only to keep DOM clean
  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  }, []);

  // Track selection changes and report to parent
  useEffect(() => {
    if (!isEditing) return;
    const handleSelectionChange = () => {
      const el = internalRef.current;
      if (!el) return;
      const offsets = getSelectionOffsets(el);
      if (!offsets) return;
      const segs = editToSegments(edit);
      const styles = getStylesAtOffset(segs, offsets.start);
      onSelectionChange?.({ editId: edit.id, start: offsets.start, end: offsets.end, styles });
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, [isEditing, edit, onSelectionChange]);

  const fontFamily = segments[0]?.styles?.fontFamily || 'Helvetica';
  const fontSize = segments[0]?.styles?.fontSize || 14;
  const s = edit.styles || {};

  // No on-screen padding: the box hugs the glyphs so adjacent text boxes
  // can be selected without their hitboxes overlapping, and so the bounding
  // box doesn't sit on top of underlines drawn beneath. The dashed selection
  // border and caret provide enough affordance for edit state.
  const containerStyle = {
    width: '100%',
    minHeight: '100%',
    textAlign: s.alignment || 'left',
    padding: 0,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    overflow: 'visible',
    fontSize: `${fontSize * zoom}px`,
    fontFamily: edit.textLayoutVersion === 2 ? `"Scrivar ${editorFontFamily(fontFamily)}"` : cssFontFamily(fontFamily),
    fontKerning: 'none', fontVariantLigatures: 'none',
    lineHeight: s.lineHeight || 1.3,
    backgroundColor: s.backgroundColor && s.backgroundColor !== 'transparent'
      ? s.backgroundColor
      : 'transparent',
    borderRadius: s.backgroundColor && s.backgroundColor !== 'transparent' ? '2px' : undefined,
    outline: 'none',
    border: 'none',
    cursor: isEditing ? 'text' : 'inherit',
  };

  return (
    <div
      ref={internalRef}
      contentEditable={isEditing}
      suppressContentEditableWarning
      style={containerStyle}
      onInput={handleInput}
      onCompositionStart={() => { composingRef.current = true; }}
      onCompositionEnd={() => { composingRef.current = false; handleInput(); }}
      role={isEditing ? "textbox" : undefined}
      aria-label={isEditing ? "Edit text" : undefined}
      aria-multiline={isEditing ? true : undefined}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onMouseDown={isEditing ? (e) => e.stopPropagation() : undefined}
    >
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// ResizeHandles — 8-point resize (4 corners + 4 edges)
// ────────────────────────────────────────────────────────────────────

function ResizeHandles({ onMouseDown, zoom }) {
  return (
    <>
      {HANDLES.map((h) => (
        <div
          key={h.id}
          onMouseDown={(e) => { e.stopPropagation(); onMouseDown('resize', h, e); }}
          className={`bg-brand-blue border-2 border-white shadow-sm ${h.isCorner ? 'rounded-sm' : 'rounded-full'}`}
          style={handlePosition(h, zoom)}
        />
      ))}
    </>
  );
}

// ────────────────────────────────────────────────────────────────────
// DeleteButton — small red X in top-right corner
// ────────────────────────────────────────────────────────────────────

function DeleteButton({ onClick }) {
  return (
    <button
      onClick={onClick}
      className="absolute -top-2.5 -right-2.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center shadow-sm hover:bg-red-600 z-10"
    >
      <X size={10} />
    </button>
  );
}
